import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, Loader2, Search, SlidersHorizontal, X, User, MapPin, Calendar, Phone } from 'lucide-react';
import PersonCardMobile from '../components/PersonCardMobile';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MissingPersonsPage = () => {
  const [cases, setCases] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    state: 'all',
    gender: 'all'
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    fetchCases();
  }, [page, filters]);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/api/states`);
      setStates(response.data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '12',
        ...(filters.search && { search: filters.search }),
        ...(filters.city && { city: filters.city }),
        ...(filters.state && filters.state !== 'all' && { state: filters.state }),
        ...(filters.gender && filters.gender !== 'all' && { gender: filters.gender })
      });

      const response = await axios.get(`${API}/api/cases/missing?${params}`);
      setCases(response.data.cases);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchQuery }));
    setPage(1);
  };

  const applyFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ search: '', city: '', state: 'all', gender: 'all' });
    setSearchQuery('');
    setPage(1);
  };

  const hasActiveFilters = filters.search || filters.city || filters.state !== 'all' || filters.gender !== 'all';

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  const genderMap = { 'male': 'पुरुष', 'female': 'महिला', 'other': 'अन्य' };

  return (
    <div className="pb-24 md:pb-12" data-testid="missing-persons-page">
      {/* ===== MOBILE HEADER ===== */}
      <div className="md:hidden bg-red-600 text-white p-6 pb-8 rounded-b-[2rem]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-outfit font-bold text-xl">लापता व्यक्ति</h1>
            <p className="text-white/70 text-sm">{total} केस दर्ज</p>
          </div>
        </div>

        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="नाम या शहर से खोजें..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 rounded-xl bg-white/90 border-0 text-gray-900 placeholder:text-gray-500"
              data-testid="search-input"
            />
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="secondary" size="icon" className="rounded-xl bg-white/20 border-0 text-white hover:bg-white/30">
                <SlidersHorizontal className="w-4 h-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-[2rem]">
              <SheetHeader>
                <SheetTitle>फ़िल्टर</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-6">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">शहर</label>
                  <Input placeholder="शहर" value={filters.city} onChange={(e) => applyFilters({ city: e.target.value })} className="rounded-xl" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">राज्य</label>
                  <Select value={filters.state} onValueChange={(v) => applyFilters({ state: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="राज्य" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">सभी राज्य</SelectItem>
                      {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">लिंग</label>
                  <Select value={filters.gender} onValueChange={(v) => applyFilters({ gender: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="लिंग" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">सभी</SelectItem>
                      <SelectItem value="male">पुरुष</SelectItem>
                      <SelectItem value="female">महिला</SelectItem>
                      <SelectItem value="other">अन्य</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {hasActiveFilters && <Button variant="outline" className="w-full rounded-xl" onClick={clearFilters}>फ़िल्टर हटाएं</Button>}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* ===== DESKTOP HEADER ===== */}
      <div className="hidden md:block py-8 border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-red-600" />
              </div>
              <div>
                <h1 className="font-outfit font-bold text-3xl text-gray-900">लापता व्यक्ति</h1>
                <p className="text-gray-500">कुल {total} केस दर्ज</p>
              </div>
            </div>
          </div>

          {/* Desktop Search & Filters */}
          <div className="flex gap-4 items-end">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="नाम या शहर से खोजें..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-12 h-12 rounded-xl text-base"
              />
            </div>
            <Select value={filters.state} onValueChange={(v) => applyFilters({ state: v })}>
              <SelectTrigger className="w-48 h-12 rounded-xl"><SelectValue placeholder="राज्य" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">सभी राज्य</SelectItem>
                {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.gender} onValueChange={(v) => applyFilters({ gender: v })}>
              <SelectTrigger className="w-36 h-12 rounded-xl"><SelectValue placeholder="लिंग" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">सभी</SelectItem>
                <SelectItem value="male">पुरुष</SelectItem>
                <SelectItem value="female">महिला</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} className="h-12 px-8 rounded-xl bg-red-600 hover:bg-red-700">
              खोजें
            </Button>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters} className="h-12 rounded-xl">
                <X className="w-4 h-4 mr-2" /> हटाएं
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="px-4 md:px-0 md:py-8 -mt-4 md:mt-0">
        <div className="md:max-w-7xl md:mx-auto md:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : cases.length === 0 ? (
            <div className="text-center py-16 app-card">
              <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">कोई केस नहीं मिला</p>
            </div>
          ) : (
            <>
              {/* Mobile List */}
              <div className="md:hidden space-y-4">
                {cases.map((person, index) => (
                  <PersonCardMobile key={person.case_id} person={person} type="missing" index={index} />
                ))}
              </div>

              {/* Desktop Grid */}
              <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                {cases.map((person, index) => (
                  <motion.div
                    key={person.case_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link to={`/case/${person.case_id}`} data-testid={`person-card-${person.case_id}`}>
                      <div className="app-card card-hover overflow-hidden">
                        <div className="aspect-[4/5] bg-gray-100 relative">
                          {person.photo_url ? (
                            <img src={getPhotoUrl(person.photo_url)} alt={person.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><User className="w-16 h-16 text-gray-300" /></div>
                          )}
                          <div className="absolute top-3 left-3">
                            <span className="status-badge-glass px-3 py-1.5 text-xs font-bold rounded-full">लापता</span>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-16">
                            <h3 className="font-outfit font-bold text-lg text-white truncate">{person.name}</h3>
                            <p className="text-white/80 text-sm">{person.age} वर्ष, {genderMap[person.gender] || person.gender}</p>
                          </div>
                        </div>
                        <div className="p-4 space-y-2">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span className="truncate">{person.city}, {person.state}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span>{person.last_seen_date}</span>
                          </div>
                          {person.reporter_mobile && (
                            <a href={`tel:${person.reporter_mobile}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 text-sm text-red-600 font-medium hover:underline">
                              <Phone className="w-4 h-4" />{person.reporter_mobile}
                            </a>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <Button variant="outline" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="rounded-xl">
                पिछला
              </Button>
              <span className="text-gray-600">{page} / {totalPages}</span>
              <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} className="rounded-xl">
                अगला
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MissingPersonsPage;
